import { useState } from 'react';
import { OzetSerit } from '../components/satis-analiz/OzetSerit';
import { ZamanBlok } from '../components/satis-analiz/ZamanBlok';
import { KanalBlok } from '../components/satis-analiz/KanalBlok';
import { SubeBlok } from '../components/satis-analiz/SubeBlok';
import { KategoriBlok } from '../components/satis-analiz/KategoriBlok';
import { UyarilarBlok } from '../components/satis-analiz/UyarilarBlok';
import { CokSatanBlok } from '../components/satis-analiz/CokSatanBlok';
import { IsiHaritasiBlok } from '../components/satis-analiz/IsiHaritasiBlok';
import { SepetBlok } from '../components/satis-analiz/SepetBlok';
import { StokSatisBlok } from '../components/satis-analiz/StokSatisBlok';
import { KarKoprusuBlok } from '../components/satis-analiz/KarKoprusuBlok';
import { FiltreSeridi } from '../components/satis-analiz/FiltreSeridi';
import { useSubeler } from '../components/satis-analiz/useSubeler';
import { useSatisAnalizFiltre } from '../components/satis-analiz/useSatisAnalizFiltre';
import { useSatisKaynaklari } from '../components/satis-analiz/ekranKaynaklari';
import { FiltreBarPortal, useFiltreBarYuvasi } from '../components/FiltreBarYuvasi';
import { SekmeSeridi } from '../components/satis-analiz/SekmeSeridi';
import { ZararBlok } from '../components/satis-analiz/ZararBlok';
import { GunDagilimBlok } from '../components/satis-analiz/GunDagilimBlok';
import { KanalSeyirBlok } from '../components/satis-analiz/KanalSeyirBlok';
import { SubeKategoriBlok } from '../components/satis-analiz/SubeKategoriBlok';
import { MarjSeyirBlok } from '../components/satis-analiz/MarjSeyirBlok';
import { KategoriMarjBlok } from '../components/satis-analiz/KategoriMarjBlok';
import { UrunKarBlok } from '../components/satis-analiz/UrunKarBlok';
import { useUyariSayisi } from '../components/satis-analiz/useUyariSayisi';
import { SEKMELER, sekmeVarMi, VARSAYILAN_SEKME, type SaSekme } from '../components/satis-analiz/sekme';
import { olcuSecilebilir, type SaOlcu } from '../components/satis-analiz/olcu';
import { HUCRE } from '../components/satis-analiz/yerlesim';
import { DetayModal } from '../components/satis-analiz/detay/DetayModal';
import type { SaDetay } from '../components/satis-analiz/detay/detayTipleri';
import type { PanelScreen, ReportConfig } from '../types/panelConfig';

interface SatisAnalizProps {
  screen: PanelScreen;
  reports: ReportConfig[];
}

/**
 * Satis Analiz — S256 tek ekran (G2), G2b duzeni. Yerlesim SABIT; `config.bloklar`'a bagli DEGIL. Filtre durumu
 * URL'de. Ilk acilis 7 istek (Genel sekmesi); diger sekmelerin bloklari sekme acilana kadar sorgu atmaz.
 * G2b: sayfa basligi yok (kirinti navbar'da); filtreler header'in hemen altindaki filtre barinda (Yerlice gibi).
 * K7 (G2b): tiklanan kategori/kanal/sube/gun/uyari/urun ortada DetayModal acar; modal acilana kadar detay sorgusu yok.
 */
export function SatisAnaliz({ screen, reports }: SatisAnalizProps) {
  const filtre = useSatisAnalizFiltre();
  const { donem, oncekiDonem } = filtre;
  const kaynaklar = useSatisKaynaklari(screen, reports);
  const { subeler, yuklendi } = useSubeler(kaynaklar.kanal040, donem);
  const { hedef } = useFiltreBarYuvasi();

  // URL'deki sube listede yoksa (o pencerede satis yok / kapanmis) TUM subelere donulur; liste
  // gelmeden sube gecersiz sayilmaz.
  const sube =
    filtre.sube === null ? null : !yuklendi || subeler.includes(filtre.sube) ? filtre.sube : null;
  const kanal = filtre.kanal;

  // K7: modal state SAYFADA (hucrede degil). Bloklar yalniz "hangi detay" bildirir.
  const [detay, setDetay] = useState<SaDetay | null>(null);

  // Grafigin olcusu. Secim kalir; donem/kanal onu gecersiz kilarsa (ör. tek gunde kar) ciroya duser, geri gelince doner.
  const [secilenOlcu, setSecilenOlcu] = useState<SaOlcu>('ciro');
  const olcuBaglami = { tekGun: donem.tekGun, kanal, karVar: !!kaynaklar.kategori && !!kaynaklar.gunlukKar };
  const olcu: SaOlcu = olcuSecilebilir(secilenOlcu, olcuBaglami) ? secilenOlcu : 'ciro';

  const uyariSayisi = useUyariSayisi(kaynaklar, donem, oncekiDonem, sube, kanal);
  const gorunenSekmeler = SEKMELER.filter((s) => sekmeVarMi(s.anahtar, kaynaklar)).map((s) =>
    s.anahtar === 'genel' ? { ...s, rozet: uyariSayisi, rozetEtiketi: `${uyariSayisi} uyarı` } : s,
  );
  const sekme: SaSekme = sekmeVarMi(filtre.sekme, kaynaklar) ? filtre.sekme : VARSAYILAN_SEKME;

  const serit = <FiltreSeridi filtre={filtre} subeler={subeler} sube={sube} kanal={kanal} donem={donem} />;

  return (
    <div className="flex flex-col gap-4">
      {hedef !== null ? (
        // Header'in hemen altinda tam genislik bar (kullanici 2026-09-15, Yerlice `.filtre-bar`: yuzey zemin, alt
        // kenarlik, hafif golge, ~0.7rem dikey dolgu). Ic hiza Layout'un icerik kabiyla ayni: max-w-[1400px],
        // < 768 px p-4, ustu p-6 (useBreakpoint).
        <FiltreBarPortal>
          <div data-testid="filtre-bar" className="bg-surface border-b border-border shadow-[0_1px_3px_rgb(16_33_92/0.06)]">
            <div className="mx-auto w-full max-w-[1400px] px-4 md:px-6 py-2.5">{serit}</div>
          </div>
        </FiltreBarPortal>
      ) : (
        // Yuvasiz kabuk: sayfa icinde yapisik serit. `bg-page` SART: `bg-surface2` derlenmis CSS'te yok, serit seffaf
        // kalir ve kartlar altindan gecerken gorunurdu.
        <div data-testid="filtre-seridi-dar" className="sticky top-0 z-10 bg-page py-2">
          {serit}
        </div>
      )}

      {/* Satir 1 — dort kart (telefonda tek kolon) */}
      <OzetSerit
        kaynaklar={kaynaklar}
        donem={donem}
        oncekiDonem={oncekiDonem}
        sube={sube}
        kanal={kanal}
        olcu={olcu}
        onOlcuSec={(o) => {
          setSecilenOlcu(o);
          // Grafik Genel'de: baska sekmedeyken karta basmak grafigi gosterir.
          if (sekme !== VARSAYILAN_SEKME) filtre.degistir({ sekme: VARSAYILAN_SEKME });
        }}
      />

      <SekmeSeridi
        sekmeler={gorunenSekmeler}
        secili={sekme}
        onSec={(yeni) => filtre.degistir({ sekme: yeni })}
      />

      {/* Yalniz secili sekmenin bloklari mount olur: acilmayan sekme sorgu atmaz (ilk acilis 7 istek, spec §4.4). */}
      <div id="sekme-paneli" role="tabpanel" aria-labelledby={`sekme-${sekme}`} data-testid={`sekme-paneli-${sekme}`} className="flex flex-col gap-4">
        {sekme === 'genel' && (
          <>
            <div data-testid="satir-genel" className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4">
              <div className={HUCRE}>
                <ZamanBlok
                  kaynaklar={kaynaklar}
                  donem={donem}
                  donemTuru={filtre.donemTuru}
                  sube={sube}
                  kanal={kanal}
                  olcu={olcu}
                  onGunSec={(gun) => setDetay({ tur: 'gun', gun })}
                />
              </div>
              <div className={HUCRE}>
                <UyarilarBlok
                  kaynaklar={kaynaklar}
                  donem={donem}
                  oncekiDonem={oncekiDonem}
                  sube={sube}
                  kanal={kanal}
                  onUyariSec={setDetay}
                />
              </div>
            </div>
            <div data-testid="satir-genel-2" className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 empty:hidden">
              {kaynaklar.isi042 && (
                <div className={HUCRE}>
                  <IsiHaritasiBlok kaynak={kaynaklar.isi042} donem={donem} sube={sube} kanal={kanal} />
                </div>
              )}
              <div className={HUCRE}>
                <GunDagilimBlok kaynak={kaynaklar.kanal040} donem={donem} sube={sube} kanal={kanal} />
              </div>
            </div>
          </>
        )}

        {sekme === 'kanal' && (
          <>
          <KanalSeyirBlok kaynak={kaynaklar.kanal040} donem={donem} donemTuru={filtre.donemTuru} sube={sube} kanal={kanal} />
          <div data-testid="satir-kanal" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={HUCRE}>
              <KanalBlok
                kaynak={kaynaklar.kanal040}
                donem={donem}
                oncekiDonem={oncekiDonem}
                sube={sube}
                kanal={kanal}
                onKanalSec={(k) => setDetay({ tur: 'kanal', kanal: k })}
              />
            </div>
            <div className={HUCRE}>
              <SubeBlok
                kaynak={kaynaklar.kanal040}
                donem={donem}
                sube={sube}
                kanal={kanal}
                onSubeSec={(s) => setDetay({ tur: 'sube', sube: s })}
              />
            </div>
          </div>
          <SubeKategoriBlok
            kaynak={kaynaklar.subeKategori017}
            donem={donem}
            sube={sube}
            kanal={kanal}
            onSubeSec={(s) => setDetay({ tur: 'sube', sube: s })}
            onKategoriSec={(k) => setDetay({ tur: 'kategori', kategori: k })}
          />
          </>
        )}

        {sekme === 'urun' && (
          <>
            <div data-testid="satir-urun" className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4">
              <div className={HUCRE}>
                <KategoriBlok
                  ayrintili
                  kaynak={kaynaklar.kategori}
                  donem={donem}
                  oncekiDonem={oncekiDonem}
                  sube={sube}
                  kanal={kanal}
                  onKategoriSec={(k) => setDetay({ tur: 'kategori', kategori: k })}
                  onDigerSec={() => setDetay({ tur: 'digerKategoriler' })}
                />
              </div>
              <div className={HUCRE}>
                <CokSatanBlok
                  ayrintili
                  onTumunuAc={kaynaklar.urunKarliligi ? () => setDetay({ tur: 'tumUrunler' }) : undefined}
                  kaynak={kaynaklar.enCokSatan}
                  donem={donem}
                  sube={sube}
                  kanal={kanal}
                  onUrunSec={(u) => setDetay({ tur: 'urun', stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
                />
              </div>
            </div>
            <div data-testid="satir-urun-2" className="grid grid-cols-1 lg:grid-cols-2 gap-4 empty:hidden">
              {kaynaklar.cokSatan014 && kaynaklar.kritik025 && (
                <div className={HUCRE}>
                  <StokSatisBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
                </div>
              )}
              {kaynaklar.sepet019 && (
                <div className={HUCRE}>
                  <SepetBlok reports={reports} sube={sube} kanal={kanal} />
                </div>
              )}
            </div>
          </>
        )}

        {sekme === 'kar' && (
          <>
            <div data-testid="satir-kar" className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4">
              {kaynaklar.urunKarliligi && (
                <div className={HUCRE}>
                  <KarKoprusuBlok kaynaklar={kaynaklar} donem={donem} sube={sube} kanal={kanal} />
                </div>
              )}
              <div className={HUCRE}>
                <MarjSeyirBlok kaynak={kaynaklar.gunlukKar} donem={donem} donemTuru={filtre.donemTuru} sube={sube} kanal={kanal} />
              </div>
            </div>
            <div data-testid="satir-kar-2" className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4">
              <div className={HUCRE}>
                <KategoriMarjBlok
                  kaynak={kaynaklar.kategori}
                  donem={donem}
                  oncekiDonem={oncekiDonem}
                  sube={sube}
                  kanal={kanal}
                  onKategoriSec={(k) => setDetay({ tur: 'kategori', kategori: k })}
                />
              </div>
              <div className={HUCRE}>
                <ZararBlok
                  kaynaklar={kaynaklar}
                  donem={donem}
                  oncekiDonem={oncekiDonem}
                  sube={sube}
                  kanal={kanal}
                  ac={setDetay}
                />
              </div>
            </div>
            <UrunKarBlok
              kaynak={kaynaklar.urunKarliligi}
              donem={donem}
              sube={sube}
              kanal={kanal}
              onUrunSec={(u) => setDetay({ tur: 'urun', stokKodu: u.stokKodu, urunAdi: u.urunAdi })}
            />
          </>
        )}
      </div>

      <DetayModal
        detay={detay}
        onKapat={() => setDetay(null)}
        kaynaklar={kaynaklar}
        donem={donem}
        oncekiDonem={oncekiDonem}
        sube={sube}
        kanal={kanal}
      />
    </div>
  );
}
